import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  /** Pixel dimensions of ogImage. Omitted from output when a custom ogImage is passed without them. */
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogImageAlt?: string;
  schema?: object | object[];
  noindex?: boolean;
}

const SITE_NAME = "HomeHelpy";
const BASE_URL = "https://www.homehelpy.com";
const DEFAULT_OG_IMAGE = `${BASE_URL}/home/carou1.webp`;
const DEFAULT_OG_IMAGE_WIDTH = 1920;
const DEFAULT_OG_IMAGE_HEIGHT = 1280;

export default function SEO({
  title,
  description,
  canonical,
  ogImage,
  ogImageWidth,
  ogImageHeight,
  ogImageAlt,
  schema,
  noindex,
}: SEOProps) {
  const fullTitle = `${title} | ${SITE_NAME}`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : undefined;
  const image = ogImage
    ? ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`
    : DEFAULT_OG_IMAGE;
  const imageWidth = ogImage ? ogImageWidth : DEFAULT_OG_IMAGE_WIDTH;
  const imageHeight = ogImage ? ogImageHeight : DEFAULT_OG_IMAGE_HEIGHT;
  const imageAlt = ogImageAlt ?? fullTitle;
  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      {imageWidth && <meta property="og:image:width" content={String(imageWidth)} />}
      {imageHeight && <meta property="og:image:height" content={String(imageHeight)} />}
      <meta property="og:image:alt" content={imageAlt} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
